import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  fetchDocuments,
  createDocument,
  updateDocument,
  updateDocumentStatus,
  deleteDocument,
  DOCUMENT_TYPES,
  DOCUMENT_STATUSES
} from '../../api/documents';
import { fetchSuppliers } from '../../api/suppliers';
import { fetchStores } from '../../api/stores';
import { getCompany } from '../../api/company';
import { buildA4SalesDocumentHtml, openPrintableDocument } from '../../utils/salesDocumentPrint';
import ScanDocumentOCR from './ScanDocumentOCR';

const TYPE_LABELS = {
  purchase: 'Purchase',
  stock_return: 'Stock Return',
  sales: 'Sales',
  refund: 'Refund',
  proforma: 'Proforma / Quotation',
  inventory_count: 'Inventory Count',
  loss_damage: 'Loss & Damage'
};

const DOCUMENT_TYPE_OPTIONS = [
  { category: 'Expenses', code: '100', label: 'Purchase', type: 'purchase' },
  { category: 'Expenses', code: '120', label: 'Stock Return', type: 'stock_return' },
  { category: 'Sales', code: '200', label: 'Sales', type: 'sales' },
  { category: 'Sales', code: '220', label: 'Refund', type: 'refund' },
  { category: 'Sales', code: '230', label: 'Proforma / Quotation', type: 'proforma' },
  { category: 'Inventory', code: '300', label: 'Inventory Count', type: 'inventory_count' },
  { category: 'Loss', code: '400', label: 'Loss & Damage', type: 'loss_damage' }
];

const QUICK_FILTER_TYPES = [
  { label: 'Sales', type: 'sales' },
  { label: 'Inventory', type: 'inventory_count' },
  { label: 'Loss', type: 'loss_damage' }
];

function formatNumber(n) {
  return Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function shouldShowItemPrices(doc) {
  return doc?.printOptions?.showItemPrices !== false;
}

function documentToForm(doc) {
  const hasItemDiscounts = (doc.items || []).some((item) => (Number(item.discountPercent) || 0) > 0 || (Number(item.discountAmount) || 0) > 0);
  return {
    type: doc.type,
    items: (doc.items || []).map((item) => ({
      productId: item.productId,
      productName: item.productName,
      sku: item.sku || '',
      unitOfMeasurement: item.unitOfMeasurement || 'pc',
      comments: Array.isArray(item.comments) ? item.comments : [],
      quantity: item.quantity ?? 1,
      unitCost: item.unitCost ?? '',
      discountPercent: Number(item.discountPercent) || 0,
      discountAmount: Number(item.discountAmount) || 0,
      quantityOnHandAfter: item.quantityOnHandAfter ?? '',
      quantityOnHandBefore: item.quantityOnHandBefore,
      ocrDescription: ''
    })),
    reason: doc.reason || '',
    reference: doc.reference || '',
    status: doc.status || 'draft',
    supplierId: doc.supplierId?._id || doc.supplierId || '',
    storeId: doc.storeId?._id || doc.storeId || '',
    date: doc.date ? new Date(doc.date).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
    dueDate: doc.dueDate ? new Date(doc.dueDate).toISOString().slice(0, 10) : (doc.date ? new Date(doc.date).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10)),
    externalReference: doc.externalReference || '',
    orderId: doc.orderId?._id || doc.orderId || '',
    source: doc.source || 'manual',
    customerName: doc.customerName || '',
    customerPhone: doc.customerPhone || '',
    customerEmail: doc.customerEmail || '',
    customerAddress: doc.customerAddress || '',
    paymentMethod: doc.paymentMethod || '',
    paymentStatus: doc.paymentStatus || '',
    paymentReference: doc.paymentReference || '',
    paidAmount: Number(doc.paidAmount) || 0,
    changeAmount: Number(doc.changeAmount) || 0,
    totalAmount: Number(doc.totalAmount) || 0,
    taxAmount: Number(doc.taxAmount) || 0,
    discountAmount: hasItemDiscounts ? 0 : (Number(doc.discountAmount) || 0),
    printOptions: {
      showItemPrices: shouldShowItemPrices(doc)
    },
    createdBy: doc.createdBy || '',
    storeName: doc.storeId?.name || ''
  };
}

export default function DocumentsManager({ authConfig, backendUrl, onUnauthorized }) {
  const [documents, setDocuments] = useState([]);
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStore, setFilterStore] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFrom, setFilterFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [filterTo, setFilterTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [filterReference, setFilterReference] = useState('');
  const [activeTab, setActiveTab] = useState('list');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    type: 'purchase',
    items: [{ productId: '', quantity: 1, unitCost: '', discountPercent: 0, discountAmount: 0, quantityOnHandAfter: '', ocrDescription: '' }],
    reason: '',
    reference: '',
    status: 'completed',
    supplierId: '',
    date: new Date().toISOString().slice(0, 10),
    dueDate: new Date().toISOString().slice(0, 10),
    externalReference: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    customerAddress: '',
    paymentMethod: '',
    paymentStatus: '',
    paymentReference: '',
    paidAmount: 0,
    changeAmount: 0,
    totalAmount: 0,
    taxAmount: 0,
    discountAmount: 0,
    printOptions: {
      showItemPrices: true
    }
  });
  const [discountMode, setDiscountMode] = useState('amount');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [showScanOCR, setShowScanOCR] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [showSelectTypeModal, setShowSelectTypeModal] = useState(false);
  const [selectedTypeOption, setSelectedTypeOption] = useState(DOCUMENT_TYPE_OPTIONS[0]);
  const [editingDocumentId, setEditingDocumentId] = useState(null);
  const [detailTab, setDetailTab] = useState('items');
  const [productSearch, setProductSearch] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [addItemFlyout, setAddItemFlyout] = useState(null);
  const [showInventoryDateTimeModal, setShowInventoryDateTimeModal] = useState(false);
  const [inventoryDateTime, setInventoryDateTime] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - 1);
    return d.toISOString().slice(0, 16);
  });
  const [company, setCompany] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType) params.type = filterType;
      if (filterStore) params.storeId = filterStore;
      if (filterStatus) params.status = filterStatus;
      if (filterFrom) params.from = filterFrom;
      if (filterTo) params.to = filterTo;
      if (filterReference.trim()) params.reference = filterReference.trim();
      const list = await fetchDocuments(params, authConfig);
      setDocuments(list);
      setError('');
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError('Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStore, filterStatus, filterFrom, filterTo, filterReference, authConfig, onUnauthorized]);

  const loadStores = useCallback(async () => {
    try {
      const list = await fetchStores();
      setStores(Array.isArray(list) ? list : []);
    } catch {
      setStores([]);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${backendUrl}/api/products`, authConfig);
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
    }
  }, [backendUrl, authConfig, onUnauthorized]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleSearch = () => loadDocuments();
  const handleClearFilters = () => {
    setFilterType('');
    setFilterStore('');
    setFilterStatus('');
    setFilterFrom(new Date().toISOString().slice(0, 10));
    setFilterTo(new Date().toISOString().slice(0, 10));
    setFilterReference('');
    setSelectedDoc(null);
  };

  const getPrintCompany = async () => {
    if (company) return company;
    try {
      const data = await getCompany(authConfig);
      setCompany(data || {});
      return data || {};
    } catch {
      setCompany({});
      return {};
    }
  };

  const printDocument = async (doc) => {
    if (!doc) return;
    const printCompany = await getPrintCompany();
    const html = buildA4SalesDocumentHtml({
      company: printCompany,
      sale: { ...doc, totalAmount: doc.totalAmount || getDocTotal(doc) },
      documentType: doc.type || 'invoice'
    });
    openPrintableDocument(html);
  };

  const getFormAsDoc = () => ({
    ...form,
    items: form.items,
    supplierId: form.supplierId ? suppliers.find((s) => s._id === form.supplierId) : null
  });

  const handlePrint = async () => {
    const doc = activeTab === 'printpreview' && previewDoc
      ? previewDoc
      : activeTab === 'detail'
        ? getFormAsDoc()
        : selectedDoc;
    if (doc) {
      await printDocument(doc);
    } else {
      window.print();
    }
  };

  const openPrintPreview = () => {
    const doc = activeTab === 'detail' ? getFormAsDoc() : selectedDoc;
    if (!doc) return;
    setPreviewDoc(doc);
    setActiveTab('printpreview');
  };

  const handleSavePdf = () => {
    handlePrint(); // Browser print dialog allows "Save as PDF"
  };

  const handleDelete = async () => {
    if (!selectedDoc) return;
    if (!window.confirm('Delete this document?')) return;
    try {
      await deleteDocument(selectedDoc._id, authConfig);
      setSelectedDoc(null);
      await loadDocuments();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.error || 'Failed to delete document.');
    }
  };

  const getLineGrossTotal = (item) => (Number(item?.quantity) || 0) * (Number(item?.unitCost) || 0);

  const getLineDiscount = (item) => {
    const gross = getLineGrossTotal(item);
    const percent = Math.min(100, Math.max(0, Number(item?.discountPercent) || 0));
    const amount = Math.max(0, Number(item?.discountAmount) || 0);
    return Math.min(gross, (gross * percent / 100) + amount);
  };

  const getLineNetTotal = (item) => Math.max(0, getLineGrossTotal(item) - getLineDiscount(item));

  const getItemsGrossTotal = (items = []) => (
    (items || []).reduce((sum, item) => sum + getLineGrossTotal(item), 0)
  );

  const getItemsDiscountTotal = (items = []) => (
    (items || []).reduce((sum, item) => sum + getLineDiscount(item), 0)
  );

  const getDocTotal = (doc) => {
    if (Number(doc?.totalAmount) > 0 || doc?.source === 'pos') return Number(doc?.totalAmount) || 0;
    if (!doc?.items?.length) return 0;
    const subtotal = getItemsGrossTotal(doc.items);
    const discount = Number(doc.discountAmount) || getItemsDiscountTotal(doc.items);
    return Math.max(0, subtotal - discount + (Number(doc.taxAmount) || 0));
  };

  const isCustomerDocumentType = (type) => ['sales', 'refund', 'proforma'].includes(type);

  const getFormItemsTotal = (items = form.items) => getItemsGrossTotal(items);

  const hasProductDiscounts = (items = form.items) => getItemsDiscountTotal(items) > 0;

  const getDiscountLabel = (item) => {
    const parts = [];
    const percent = Number(item?.discountPercent) || 0;
    const amount = Number(item?.discountAmount) || 0;
    if (percent > 0) parts.push(`${formatNumber(percent).replace(/\.00$/, '')}%`);
    if (amount > 0) parts.push(`P${formatNumber(amount)}`);
    return parts.length ? parts.join(' + ') : '0';
  };

  const clampDiscountAmount = (value, items = form.items) => {
    const subtotal = getFormItemsTotal(items);
    return Math.min(subtotal, Math.max(0, Number(value) || 0));
  };

  const getFormDiscount = (items = form.items) => {
    const productDiscount = getItemsDiscountTotal(items);
    if (productDiscount > 0) return productDiscount;
    const subtotal = getFormItemsTotal(items);
    if (discountMode === 'percent') {
      const percent = Math.min(100, Math.max(0, Number(discountPercent) || 0));
      return Math.min(subtotal, subtotal * (percent / 100));
    }
    return clampDiscountAmount(form.discountAmount, items);
  };

  const getFormTax = () => Math.max(0, Number(form.taxAmount) || 0);

  const getFormTotal = (items = form.items) => {
    const subtotal = getFormItemsTotal(items);
    return Math.max(0, subtotal - getFormDiscount(items) + getFormTax());
  };

  const openDocumentForEdit = (doc) => {
    if (!doc) return;
    setSelectedDoc(doc);
    setForm(documentToForm(doc));
    setDiscountMode('amount');
    setDiscountPercent(0);
    setEditingDocumentId(doc._id);
    setDetailTab('items');
    setActiveTab('detail');
  };

  const buildDocumentItemsPayload = () => {
    if (form.type === 'inventory_count') {
      return form.items
        .filter((r) => r.productId && (r.quantityOnHandAfter !== '' && r.quantityOnHandAfter !== undefined))
        .map((r) => {
          const product = products.find((p) => p._id === r.productId);
          return {
            productId: r.productId,
            productName: r.productName || product?.name,
            sku: r.sku || product?.sku || '',
            unitOfMeasurement: r.unitOfMeasurement || product?.unitOfMeasurement || 'pc',
            quantity: 1,
            quantityOnHandBefore: r.quantityOnHandBefore ?? product?.quantityOnHand,
            quantityOnHandAfter: Number(r.quantityOnHandAfter),
            comments: Array.isArray(r.comments) ? r.comments : []
          };
        });
    }

    return form.items
      .filter((r) => r.productId && Number(r.quantity) > 0)
      .map((r) => {
        const product = products.find((p) => p._id === r.productId);
        return {
          productId: r.productId,
          productName: r.productName || product?.name,
          sku: r.sku || product?.sku || '',
          unitOfMeasurement: r.unitOfMeasurement || product?.unitOfMeasurement || 'pc',
          quantity: Number(r.quantity),
          unitCost: r.unitCost !== '' ? Number(r.unitCost) : undefined,
          discountPercent: Math.min(100, Math.max(0, Number(r.discountPercent) || 0)),
          discountAmount: Math.max(0, Number(r.discountAmount) || 0),
          comments: Array.isArray(r.comments) ? r.comments : []
        };
      });
  };

  const buildDocumentPayload = (items) => {
    const computedTotal = getFormTotal(items);
    return {
      type: form.type,
      storeId: form.storeId || undefined,
      date: form.date,
      dueDate: form.dueDate || form.date,
      items,
      reason: form.reason || undefined,
      reference: form.reference || undefined,
      externalReference: form.externalReference || undefined,
      status: form.status,
      supplierId: !isCustomerDocumentType(form.type) && form.supplierId ? form.supplierId : undefined,
      customerName: isCustomerDocumentType(form.type) ? (form.customerName || 'Walk-in') : '',
      customerPhone: isCustomerDocumentType(form.type) ? (form.customerPhone || '') : '',
      customerEmail: isCustomerDocumentType(form.type) ? (form.customerEmail || '') : '',
      customerAddress: isCustomerDocumentType(form.type) ? (form.customerAddress || '') : '',
      paymentMethod: form.paymentMethod || '',
      paymentStatus: form.paymentStatus || '',
      paymentReference: form.paymentReference || '',
      paidAmount: Number(form.paidAmount) || 0,
      changeAmount: Number(form.changeAmount) || 0,
      totalAmount: computedTotal,
      taxAmount: getFormTax(),
      discountAmount: getFormDiscount(items),
      printOptions: {
        showItemPrices: shouldShowItemPrices(form)
      }
    };
  };

  const loadSuppliers = useCallback(async () => {
    try {
      const data = await fetchSuppliers({}, authConfig);
      setSuppliers(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
    }
  }, [authConfig, onUnauthorized]);

  useEffect(() => {
    if (activeTab !== 'list') {
      loadProducts();
      loadSuppliers();
    }
  }, [activeTab, loadProducts, loadSuppliers]);

  useEffect(() => {
    if (activeTab === 'printpreview') {
      getCompany(authConfig).then((c) => setCompany(c || {})).catch(() => setCompany({}));
    }
  }, [activeTab, authConfig]);

  const addLine = () => {
    setForm((f) => ({
      ...f,
      items: [...f.items, { productId: '', quantity: 1, unitCost: '', discountPercent: 0, discountAmount: 0, quantityOnHandAfter: '', ocrDescription: '' }]
    }));
  };

  const handleOcrLines = (parsedLines) => {
    if (!parsedLines || parsedLines.length === 0) return;
    const newItems = parsedLines.map((p) => ({
      productId: '',
      quantity: p.quantity || 1,
      unitCost: p.unitCost !== undefined && p.unitCost !== '' ? String(p.unitCost) : '',
      discountPercent: 0,
      discountAmount: 0,
      quantityOnHandAfter: '',
      ocrDescription: p.description || ''
    }));
    setForm((f) => ({ ...f, items: [...f.items, ...newItems] }));
    setShowScanOCR(false);
  };

  const removeLine = (index) => {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, i) => i !== index)
    }));
  };

  const updateLine = (index, field, value) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    let items;
    if (form.type === 'inventory_count') {
      items = form.items
        .filter((row) => row.productId && (row.quantityOnHandAfter !== '' && row.quantityOnHandAfter !== undefined))
        .map((row) => {
          const product = products.find((p) => p._id === row.productId);
          return {
            productId: row.productId,
            productName: product?.name,
            quantity: 1,
            quantityOnHandBefore: product?.quantityOnHand,
            quantityOnHandAfter: Number(row.quantityOnHandAfter)
          };
        });
    } else {
      items = form.items
        .filter((row) => row.productId && Number(row.quantity) > 0)
        .map((row) => ({
          productId: row.productId,
          productName: products.find((p) => p._id === row.productId)?.name,
          quantity: Number(row.quantity),
          unitCost: row.unitCost !== '' ? Number(row.unitCost) : undefined,
          discountPercent: Math.min(100, Math.max(0, Number(row.discountPercent) || 0)),
          discountAmount: Math.max(0, Number(row.discountAmount) || 0)
        }));
    }
    if (items.length === 0) {
      setError(form.type === 'inventory_count' ? 'Add at least one item with new count.' : 'Add at least one item with product and quantity.');
      return;
    }
    setCreating(true);
    setError('');
    try {
      await createDocument(
        {
          type: form.type,
          items,
          reason: form.reason || undefined,
          reference: form.reference || undefined,
          status: form.status,
          supplierId: form.type === 'purchase' && form.supplierId ? form.supplierId : undefined,
          printOptions: {
            showItemPrices: shouldShowItemPrices(form)
          }
        },
        authConfig
      );
      setForm({
        type: 'purchase',
        items: [{ productId: '', quantity: 1, unitCost: '', discountPercent: 0, discountAmount: 0, quantityOnHandAfter: '', ocrDescription: '' }],
        reason: '',
        reference: '',
        status: 'completed',
        supplierId: '',
        date: new Date().toISOString().slice(0, 10),
        dueDate: new Date().toISOString().slice(0, 10),
        printOptions: {
          showItemPrices: true
        }
      });
      setDiscountMode('amount');
      setDiscountPercent(0);
      await loadDocuments();
      setActiveTab('list');
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.error || 'Failed to create document.');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (docId, status) => {
    try {
      await updateDocumentStatus(docId, status, authConfig);
      await loadDocuments();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.error || 'Failed to update status.');
    }
  };

  const isLinkedPosDocument = Boolean(form.orderId);

  return (
    <div className="space-y-4">
      {/* Header + View documents tab */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-gray-800">Management &gt; Documents</h2>
        <button
          type="button"
          onClick={() => { setActiveTab('list'); setSelectedDoc(null); }}
          className={`px-3 py-1.5 rounded text-sm ${activeTab === 'list' ? 'bg-indigo-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
        >
          View documents
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Select document type modal */}
      {showSelectTypeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Select document type</h3>
            <div className="space-y-1 mb-6">
              {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.type}
                  type="button"
                  onClick={() => setSelectedTypeOption(opt)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded text-left text-sm ${
                    selectedTypeOption?.type === opt.type ? 'bg-indigo-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  <span>{opt.category}</span>
                  <span>{opt.code ? `${opt.code} - ${opt.label}` : opt.label}</span>
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowSelectTypeModal(false)}
                className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm border border-gray-300 hover:bg-gray-100"
              >
                <span aria-hidden>✕</span> Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const type = selectedTypeOption?.type || 'purchase';
                  setForm({
                    type,
                    items: [],
                    reason: '',
                    reference: '',
                    status: 'draft',
                    supplierId: '',
                    date: new Date().toISOString().slice(0, 10),
                    dueDate: new Date().toISOString().slice(0, 10),
                    externalReference: '',
                    customerName: '',
                    customerPhone: '',
                    customerEmail: '',
                    customerAddress: '',
                    paymentMethod: '',
                    paymentStatus: '',
                    paymentReference: '',
                    paidAmount: 0,
                    changeAmount: 0,
                    totalAmount: 0,
                    taxAmount: 0,
                    discountAmount: 0
                  });
                  setDiscountMode('amount');
                  setDiscountPercent(0);
                  setEditingDocumentId(null);
                  setDetailTab('items');
                  setShowSelectTypeModal(false);
                  if (type === 'inventory_count') {
                    const d = new Date();
                    d.setMinutes(d.getMinutes() - 1);
                    setInventoryDateTime(d.toISOString().slice(0, 16));
                    setShowInventoryDateTimeModal(true);
                  } else {
                    setActiveTab('detail');
                  }
                }}
                className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm bg-indigo-600 text-white hover:bg-indigo-500"
              >
                <span aria-hidden>✓</span> OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inventory count: select date and time when count occurred (past only) */}
      {showInventoryDateTimeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Inventory count</h3>
            <p className="text-sm text-gray-600 mb-4">Select date and time when physical count occurred (past only). Stock date cannot be changed later.</p>
            <input
              type="datetime-local"
              value={inventoryDateTime}
              max={new Date().toISOString().slice(0, 16)}
              onChange={(e) => setInventoryDateTime(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm mb-4"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowInventoryDateTimeModal(false)} className="px-4 py-2 rounded text-sm border hover:bg-gray-100">Cancel</button>
              <button
                type="button"
                onClick={() => {
                  setForm((f) => ({ ...f, date: inventoryDateTime }));
                  setShowInventoryDateTimeModal(false);
                  setActiveTab('detail');
                }}
                className="px-4 py-2 rounded text-sm bg-indigo-600 text-white hover:bg-indigo-500"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'list' ? (
        <div className="space-y-4">
          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { setSelectedTypeOption(DOCUMENT_TYPE_OPTIONS[0]); setShowSelectTypeModal(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm bg-indigo-600 text-white hover:bg-indigo-500"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add
            </button>
            <button type="button" onClick={handlePrint} disabled={!selectedDoc} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" title="Print">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
              Print
            </button>
            <button type="button" onClick={openPrintPreview} disabled={!selectedDoc} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" title="Print preview">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              Print preview
            </button>
            <button type="button" onClick={handleSavePdf} disabled={!selectedDoc} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" title="Save as PDF">
              <span className="text-xs font-bold text-red-600">PDF</span>
              Save as PDF
            </button>
            <button
              type="button"
              onClick={() => openDocumentForEdit(selectedDoc)}
              disabled={!selectedDoc}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Open document"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
              Edit
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={!selectedDoc || Boolean(selectedDoc?.orderId)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              Delete
            </button>
          </div>

          {/* Quick filters: Sales, Inventory, Loss */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-gray-600 font-medium">Find:</span>
            {QUICK_FILTER_TYPES.map((q) => (
              <button
                key={q.type}
                type="button"
                onClick={() => setFilterType(filterType === q.type ? '' : q.type)}
                className={`px-3 py-1.5 rounded text-sm ${filterType === q.type ? 'bg-indigo-600 text-white' : 'border border-gray-300 hover:bg-gray-100'}`}
              >
                {q.label}
              </button>
            ))}
            {filterType && (
              <button
                type="button"
                onClick={() => setFilterType('')}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Clear
              </button>
            )}
          </div>

          {/* Right-click context menu */}
          {contextMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} aria-hidden />
              <div className="fixed z-50 bg-white border rounded-lg shadow-lg py-1 min-w-[160px]" style={{ left: contextMenu.x, top: contextMenu.y }}>
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  onClick={() => {
                    openDocumentForEdit(contextMenu.doc);
                    setContextMenu(null);
                  }}
                >
                  {contextMenu.doc.orderId ? 'View' : 'Edit'}
                </button>
                <div className="border-t my-1" />
                <div className="px-4 py-1 text-xs text-gray-500">Paid status</div>
                <button
                  type="button"
                  disabled={Boolean(contextMenu.doc.orderId)}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={async () => {
                    try {
                      await updateDocumentStatus(contextMenu.doc._id, 'completed', authConfig);
                      await loadDocuments();
                      setContextMenu(null);
                    } catch (err) {
                      if (err?.response?.status === 401) onUnauthorized?.(err);
                      else setError(err?.response?.data?.error || 'Failed to update');
                    }
                  }}
                >
                  Mark paid
                </button>
                <button
                  type="button"
                  disabled={Boolean(contextMenu.doc.orderId)}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={async () => {
                    try {
                      await updateDocumentStatus(contextMenu.doc._id, 'draft', authConfig);
                      await loadDocuments();
                      setContextMenu(null);
                    } catch (err) {
                      if (err?.response?.status === 401) onUnauthorized?.(err);
                      else setError(err?.response?.data?.error || 'Failed to update');
                    }
                  }}
                >
                  Mark unpaid
                </button>
              </div>
            </>
          )}

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 items-end p-3 bg-gray-50 rounded border">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Document type</label>
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">All document types</option>
                {DOCUMENT_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Store</label>
              <select value={filterStore} onChange={(e) => setFilterStore(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">All stores</option>
                {stores.map((s) => <option key={s._id} value={s._id}>{s.name || 'Store'}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Status</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">All statuses</option>
                {DOCUMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Document number / reference</label>
              <input
                type="text"
                value={filterReference}
                onChange={(e) => setFilterReference(e.target.value)}
                placeholder="Search by ID or reference"
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Period</label>
              <div className="flex gap-1">
                <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="flex-1 border rounded px-2 py-1.5 text-sm min-w-0" />
                <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="flex-1 border rounded px-2 py-1.5 text-sm min-w-0" />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={handleSearch} className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-indigo-600 text-white hover:bg-indigo-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                Search
              </button>
              <button type="button" onClick={handleClearFilters} className="px-3 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-100">
                Clear all filters
              </button>
            </div>
          </div>

          {/* Documents table */}
          <div className="border rounded overflow-x-auto">
            <h3 className="px-3 py-2 text-sm font-medium bg-gray-100 border-b">Documents ({documents.length})</h3>
            {loading ? (
              <p className="p-4 text-gray-500">Loading documents...</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 border-b text-left">
                    <th className="p-2 font-medium">ID</th>
                    <th className="p-2 font-medium">Number</th>
                    <th className="p-2 font-medium">External</th>
                    <th className="p-2 font-medium">Document type</th>
                    <th className="p-2 font-medium">Status</th>
                    <th className="p-2 font-medium">Customer / Supplier</th>
                    <th className="p-2 font-medium">Date</th>
                    <th className="p-2 font-medium">Store</th>
                    <th className="p-2 font-medium">Order</th>
                    <th className="p-2 font-medium">Payment</th>
                    <th className="p-2 font-medium">User</th>
                    <th className="p-2 font-medium">Items</th>
                    <th className="p-2 font-medium">Total</th>
                    <th className="p-2 font-medium">Internal note</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.length === 0 && (
                    <tr><td colSpan={14} className="p-4 text-gray-500 text-center">No documents found.</td></tr>
                  )}
                  {documents.map((doc) => (
                    <tr
                      key={doc._id}
                      id={`doc-row-${doc._id}`}
                      onClick={() => setSelectedDoc(doc)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setSelectedDoc(doc);
                        setContextMenu({ x: e.clientX, y: e.clientY, doc });
                      }}
                      onDoubleClick={() => {
                        openDocumentForEdit(doc);
                      }}
                      className={`border-b cursor-pointer hover:bg-indigo-50 ${selectedDoc?._id === doc._id ? 'bg-indigo-100' : ''}`}
                    >
                      <td className="p-2 text-gray-600">{String(doc._id).slice(-6)}</td>
                      <td className="p-2 font-medium">{doc.reference || String(doc._id).slice(0, 8)}</td>
                      <td className="p-2">{doc.externalReference ? String(doc.externalReference).slice(-10) : '-'}</td>
                      <td className="p-2">{TYPE_LABELS[doc.type] || doc.type}</td>
                      <td className="p-2">
                        <select
                          value={doc.status}
                          onChange={(e) => { e.stopPropagation(); handleStatusChange(doc._id, e.target.value); }}
                          onClick={(e) => e.stopPropagation()}
                          disabled={Boolean(doc.orderId)}
                          title={doc.orderId ? 'Status follows the linked POS order' : ''}
                          className="border rounded px-1 text-xs max-w-[100px] disabled:bg-gray-100"
                        >
                          {DOCUMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="p-2">{doc.customerName || doc.supplierId?.name || '-'}</td>
                      <td className="p-2">{new Date(doc.date).toLocaleDateString()}</td>
                      <td className="p-2">{doc.storeId?.name || '-'}</td>
                      <td className="p-2">{doc.orderId ? String(doc.orderId).slice(-8) : '-'}</td>
                      <td className="p-2 whitespace-nowrap">
                        <div>{doc.paymentMethod || '-'}{doc.paymentStatus ? ` / ${doc.paymentStatus}` : ''}</div>
                        {doc.paymentReference && <div className="text-xs text-gray-500">Ref: {doc.paymentReference}</div>}
                      </td>
                      <td className="p-2">{doc.createdBy || '-'}</td>
                      <td className="p-2">{doc.items?.length || 0}</td>
                      <td className="p-2">P{formatNumber(getDocTotal(doc))}</td>
                      <td className="p-2 max-w-[120px] truncate" title={doc.reason}>{doc.reason || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Document items table (for selected document) */}
          <div className="border rounded overflow-x-auto">
            <h3 className="px-3 py-2 text-sm font-medium bg-gray-100 border-b">Document items ({selectedDoc?.items?.length ?? 0})</h3>
            {selectedDoc ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 border-b text-left">
                    <th className="p-2 font-medium">ID</th>
                    <th className="p-2 font-medium">Code</th>
                    <th className="p-2 font-medium">Name</th>
                    <th className="p-2 font-medium">Unit</th>
                    <th className="p-2 font-medium">Quantity</th>
                    <th className="p-2 font-medium">Price before tax</th>
                    <th className="p-2 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedDoc.items || []).map((item, idx) => (
                    <tr key={`${selectedDoc._id}-${idx}`} className="border-b">
                      <td className="p-2 text-gray-600">{String(item.productId || idx).slice(-6)}</td>
                      <td className="p-2">{item.sku || '-'}</td>
                      <td className="p-2">
                        <div>{item.productName || '-'}</div>
                        {getLineDiscount(item) > 0 && <div className="text-xs text-indigo-600 mt-0.5">Discount: {getDiscountLabel(item)}</div>}
                        {(item.comments || []).map((comment, commentIndex) => (
                          <div key={`${comment}-${commentIndex}`} className="text-xs text-gray-500 mt-0.5">
                            {/^sn\s*:/i.test(comment) ? comment : `SN / Comment: ${comment}`}
                          </div>
                        ))}
                      </td>
                      <td className="p-2">{item.unitOfMeasurement || 'pc'}</td>
                      <td className="p-2">{item.quantity ?? (item.quantityOnHandAfter ?? '-')}</td>
                      <td className="p-2">P{formatNumber(item.unitCost)}</td>
                      <td className="p-2">P{formatNumber(getLineNetTotal(item))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="p-4 text-gray-500">Select a document to view its items.</p>
            )}
          </div>
        </div>
      ) : activeTab === 'printpreview' && previewDoc ? (
        /* Aronium-style print preview */
        <div className="bg-gray-100 rounded-lg overflow-hidden flex flex-col h-[calc(100vh-8rem)] print:bg-white print:h-auto">
          <div className="flex items-center justify-between px-4 py-2 border-b bg-white print:hidden">
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setActiveTab('list')} className="px-2 py-1 rounded text-sm bg-indigo-600 text-white hover:bg-indigo-500">View documents</button>
              <span className="text-sm font-medium text-gray-700">{previewDoc.reference || String(previewDoc._id || '').slice(-12)}</span>
              <span className="text-sm text-indigo-600 font-medium">Print preview: {previewDoc.reference || String(previewDoc._id || '').slice(-12)}</span>
            </div>
          </div>
          <div className="flex items-center justify-between px-4 py-2 border-b bg-white print:hidden">
            <div className="flex gap-2">
              <button type="button" onClick={handlePrint} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-50">Print</button>
              <div className="relative">
                <button type="button" onClick={handleSavePdf} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-50">Save ▾</button>
              </div>
              <div className="flex items-center gap-1 border rounded px-2 py-1 text-sm">
                <button type="button" className="p-1 hover:bg-gray-100 rounded" title="First page">«</button>
                <button type="button" className="p-1 hover:bg-gray-100 rounded" title="Previous">‹</button>
                <span className="px-2 min-w-[60px] text-center">1 of 1</span>
                <button type="button" className="p-1 hover:bg-gray-100 rounded" title="Next">›</button>
                <button type="button" className="p-1 hover:bg-gray-100 rounded" title="Last page">»</button>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-6 flex justify-center print:p-0">
            <iframe
              title="Document print preview"
              srcDoc={buildA4SalesDocumentHtml({
                company: company || {},
                sale: { ...previewDoc, totalAmount: previewDoc.totalAmount || getDocTotal(previewDoc) },
                documentType: previewDoc.type || 'invoice'
              })}
              className="bg-white shadow-lg shrink-0 print:shadow-none"
              style={{ width: '210mm', height: '297mm', border: 0 }}
            />
            <style>{'@page { size: A4 portrait; margin: 0; } @media print { #invoice-print-preview { width: 210mm !important; min-height: 297mm !important; margin: 0 !important; padding: 15mm 16mm !important; box-shadow: none !important; } }'}</style>
            <div id="invoice-print-preview" className="hidden" style={{ width: '210mm', minHeight: '297mm' }}>
              <h1 className="text-2xl font-bold text-center mb-6">{previewDoc.type === 'proforma' ? 'PROFORMA / QUOTATION' : 'INVOICE'}</h1>
              <div className="flex justify-between items-start mb-6 gap-4">
                <div className="flex-1">
                  <p className="font-semibold text-lg">{company?.name || 'Company Name'}</p>
                  <p className="text-sm text-gray-600">{company?.address || 'Address'}</p>
                  <p className="text-sm text-gray-600">Tax No: {company?.taxId || '—'}</p>
                  <p className="text-sm text-gray-600">{company?.phone || '—'}</p>
                  <p className="text-sm text-gray-600">{company?.email || '—'}</p>
                </div>
                {company?.logo && <img src={company.logo} alt="Logo" className="h-16 object-contain" />}
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                <div>
                  <p className="font-medium text-gray-600">Bill to:</p>
                  <p>{previewDoc.customerName || previewDoc.supplierId?.name || 'Walk-in'}</p>
                  {previewDoc.customerPhone && <p className="text-gray-600">{previewDoc.customerPhone}</p>}
                  {previewDoc.customerEmail && <p className="text-gray-600">{previewDoc.customerEmail}</p>}
                  {previewDoc.customerAddress && <p className="text-gray-600">{previewDoc.customerAddress}</p>}
                </div>
                <div className="space-y-1">
                  <p><span className="text-gray-600">Invoice No:</span> {previewDoc.reference || previewDoc._id || '—'}</p>
                  <p><span className="text-gray-600">Date:</span> {previewDoc.date ? new Date(previewDoc.date).toLocaleDateString() : '—'}</p>
                  <p><span className="text-gray-600">Due date:</span> {(previewDoc.dueDate || previewDoc.date) ? new Date(previewDoc.dueDate || previewDoc.date).toLocaleDateString() : '—'}</p>
                  <p><span className="text-gray-600">Payment status:</span> {previewDoc.paymentStatus || (previewDoc.status === 'completed' ? 'Paid' : 'Unpaid')}</p>
                  <p><span className="text-gray-600">Payment method:</span> {previewDoc.paymentMethod || '—'}</p>
                  <p><span className="text-gray-600">Payment reference:</span> {previewDoc.paymentReference || '—'}</p>
                  <p><span className="text-gray-600">Cashier:</span> {previewDoc.createdBy || '—'}</p>
                  <p><span className="text-gray-600">Order:</span> {previewDoc.externalReference || previewDoc.orderId || '—'}</p>
                </div>
              </div>
              <table className="w-full text-sm border-collapse mb-6">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left py-2">#</th>
                    <th className="text-left py-2">Item</th>
                    <th className="text-right py-2">Quantity</th>
                    <th className="text-right py-2">Unit price</th>
                    <th className="text-right py-2">Tax</th>
                    <th className="text-right py-2">Discount</th>
                    <th className="text-right py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(previewDoc.items || []).map((item, idx) => {
                    const qty = item.quantity ?? 0;
                    const unitPrice = item.unitCost ?? 0;
                    const total = getLineNetTotal(item);
                    return (
                      <tr key={idx} className="border-b border-gray-200">
                        <td className="py-2">{idx + 1}</td>
                        <td className="py-2">
                          <div>{item.productName || '—'}</div>
                          {item.sku && <div className="text-xs text-gray-500">Code: {item.sku}</div>}
                          {(item.comments || []).map((comment, commentIndex) => (
                            <div key={`${comment}-${commentIndex}`} className="text-xs text-gray-500">{comment}</div>
                          ))}
                        </td>
                        <td className="py-2 text-right">{qty}</td>
                        <td className="py-2 text-right">{formatNumber(unitPrice)}</td>
                        <td className="py-2 text-right">—</td>
                        <td className="py-2 text-right">{getDiscountLabel(item)}</td>
                        <td className="py-2 text-right font-medium">{formatNumber(total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(() => {
                const subtotal = getItemsGrossTotal(previewDoc.items || []);
                const grandTotal = getDocTotal(previewDoc);
                const discount = Number(previewDoc.discountAmount) || getItemsDiscountTotal(previewDoc.items || []);
                const paidAmount = Number(previewDoc.paidAmount) || (previewDoc.paymentStatus === 'paid' || previewDoc.status === 'completed' ? grandTotal : 0);
                const amountDue = Math.max(0, grandTotal - Math.min(grandTotal, paidAmount));
                return (
                  <div className="border-t pt-4 space-y-1 text-sm">
                    <p className="text-right">Subtotal: P{formatNumber(subtotal)}</p>
                    <p className="text-right">Discount: P{formatNumber(discount)}</p>
                    <p className="text-right">Tax: P{formatNumber(previewDoc.taxAmount)}</p>
                    <p className="text-right font-semibold text-lg">Total: P{formatNumber(grandTotal)}</p>
                    <p><span className="text-gray-600">Payment method:</span> {previewDoc.paymentMethod || '—'}</p>
                    {previewDoc.paymentReference && <p><span className="text-gray-600">Reference:</span> {previewDoc.paymentReference}</p>}
                    <p><span className="text-gray-600">Paid amount:</span> P{formatNumber(paidAmount)}</p>
                    {Number(previewDoc.changeAmount) > 0 && <p><span className="text-gray-600">Change:</span> P{formatNumber(previewDoc.changeAmount)}</p>}
                    <p><span className="text-gray-600">Amount due:</span> P{formatNumber(amountDue)}</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      ) : activeTab === 'detail' ? (
        /* Aronium-style document detail view (Sales / any type) */
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden flex flex-col max-h-[calc(100vh-12rem)]">
          {/* Header: View documents + doc number + X */}
          <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setActiveTab('list')}
                className="px-2 py-1 rounded text-sm bg-indigo-600 text-white hover:bg-indigo-500"
              >
                View documents
              </button>
              <span className="text-sm font-medium text-gray-700">
                {form.reference || (editingDocumentId ? String(editingDocumentId).slice(-8) : 'New')} - {TYPE_LABELS[form.type] || form.type}
              </span>
            </div>
            <button type="button" onClick={() => { setActiveTab('list'); setEditingDocumentId(null); }} className="p-1 rounded hover:bg-gray-200" aria-label="Close">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Document fields */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 p-4 border-b">
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Number</label>
              <input type="text" value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" placeholder="Doc number" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} readOnly={form.type === 'inventory_count'} className="w-full border rounded px-2 py-1.5 text-sm read-only:bg-gray-100" title={form.type === 'inventory_count' ? 'Stock date cannot be changed for inventory count' : ''} />
            </div>
            <div className="flex items-end pb-1.5">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.paymentStatus === 'paid' || form.status === 'completed'} onChange={(e) => setForm((f) => ({ ...f, status: e.target.checked ? 'completed' : 'draft', paymentStatus: e.target.checked ? 'paid' : 'pending' }))} className="rounded" />
                <span className="text-sm">Paid</span>
              </label>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">External document</label>
              <input type="text" value={form.externalReference || ''} onChange={(e) => setForm((f) => ({ ...f, externalReference: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Due date / expiry</label>
              <input type="date" value={form.dueDate || form.date} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" title="Quotation expiration date" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">{['sales', 'refund', 'proforma'].includes(form.type) ? 'Customer' : 'Supplier'}</label>
              {isCustomerDocumentType(form.type) ? (
                <input type="text" value={form.customerName || ''} onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" placeholder="Walk-in" />
              ) : (
                <select value={form.supplierId || ''} onChange={(e) => setForm((f) => ({ ...f, supplierId: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm">
                  <option value="">—</option>
                  {suppliers.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Stock date</label>
              <span className="text-sm text-gray-600">{form.date ? new Date(form.date).toLocaleString() : '—'}</span>
            </div>
          </div>

          {isCustomerDocumentType(form.type) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 px-4 pb-4 border-b">
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Phone</label>
                <input type="text" value={form.customerPhone || ''} onChange={(e) => setForm((f) => ({ ...f, customerPhone: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Email</label>
                <input type="email" value={form.customerEmail || ''} onChange={(e) => setForm((f) => ({ ...f, customerEmail: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Address</label>
                <input type="text" value={form.customerAddress || ''} onChange={(e) => setForm((f) => ({ ...f, customerAddress: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
            </div>
          )}

          {isLinkedPosDocument && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 px-4 py-3 border-b bg-indigo-50 text-sm">
              <div><span className="block text-xs text-gray-500">Order ID</span>{form.externalReference || form.orderId}</div>
              <div><span className="block text-xs text-gray-500">Cashier</span>{form.createdBy || '-'}</div>
              <div><span className="block text-xs text-gray-500">Source</span>POS sale</div>
              <div><span className="block text-xs text-gray-500">Linked order</span>{form.orderId || '-'}</div>
            </div>
          )}

          {/* Tabs: Document items | Payments */}
          <div className="flex border-b">
            <button type="button" onClick={() => setDetailTab('items')} className={`px-4 py-2 text-sm font-medium ${detailTab === 'items' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500'}`}>
              Document items
            </button>
            <button type="button" onClick={() => setDetailTab('payments')} className={`px-4 py-2 text-sm font-medium ${detailTab === 'payments' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500'}`}>
              Payments
            </button>
          </div>

          {detailTab === 'items' ? (
            <div className="flex flex-1 min-h-0">
              {/* Left: product search + product list */}
              <div className="w-64 border-r flex flex-col shrink-0">
                <input type="text" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Product name, code or barcode" className="m-2 border rounded px-2 py-1.5 text-sm" />
                <div className="flex-1 overflow-y-auto p-2">
                  {products
                    .filter((p) => {
                      if (!productSearch.trim()) return true;
                      const q = productSearch.toLowerCase();
                      return (p.name || '').toLowerCase().includes(q) ||
                        (p.sku || '').toLowerCase().includes(q) ||
                        (p.barcode || '').toLowerCase().includes(q);
                    })
                    .map((p) => (
                      <button
                        key={p._id}
                        type="button"
                        onClick={() => setAddItemFlyout({ product: p, quantity: 1, price: p.price ?? p.cost ?? 0, counted: p.quantityOnHand ?? 0 })}
                        onDoubleClick={() => {
                          setAddItemFlyout({ product: p, quantity: 1, price: p.price ?? p.cost ?? 0, counted: p.quantityOnHand ?? 0 });
                        }}
                        className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-indigo-50 border-b border-gray-100"
                      >
                        {p.name}
                      </button>
                    ))}
                </div>
              </div>
              {/* Add item flyout: quantity and price (Aronium-style) */}
              {addItemFlyout && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
                  <div className="bg-white rounded-lg shadow-xl w-full max-w-xs mx-4 p-4" onClick={(e) => e.stopPropagation()}>
                    <h4 className="font-medium mb-2">Add item — {addItemFlyout.product?.name}</h4>
                    {form.type === 'inventory_count' ? (
                      <div className="space-y-2">
                        <label className="block text-sm">Counted quantity</label>
                        <input
                          type="number"
                          min="0"
                          value={addItemFlyout.counted}
                          onChange={(e) => setAddItemFlyout((f) => ({ ...f, counted: Number(e.target.value) || 0 }))}
                          className="w-full border rounded px-3 py-2"
                        />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <label className="block text-sm">Quantity</label>
                        <input type="number" min="0.0001" step="0.0001" value={addItemFlyout.quantity} onChange={(e) => setAddItemFlyout((f) => ({ ...f, quantity: Number(e.target.value) || 1 }))} className="w-full border rounded px-3 py-2" />
                        <label className="block text-sm">Price / Unit cost</label>
                        <input type="number" min="0" step="0.01" value={addItemFlyout.price} onChange={(e) => setAddItemFlyout((f) => ({ ...f, price: Number(e.target.value) || 0 }))} className="w-full border rounded px-3 py-2" />
                      </div>
                    )}
                    <div className="flex justify-end gap-2 mt-4">
                      <button type="button" onClick={() => setAddItemFlyout(null)} className="px-3 py-1.5 rounded text-sm border">Cancel</button>
                      <button
                        type="button"
                        onClick={() => {
                          const p = addItemFlyout.product;
                          if (form.type === 'inventory_count') {
                            setForm((f) => ({
                              ...f,
                              items: [...f.items, { productId: p._id, productName: p.name, sku: p.sku || '', unitOfMeasurement: p.unitOfMeasurement || 'pc', quantity: 1, quantityOnHandAfter: addItemFlyout.counted, quantityOnHandBefore: p.quantityOnHand, comments: [], ocrDescription: '' }]
                            }));
                          } else {
                            const idx = form.items.findIndex((i) => i.productId === p._id);
                            if (idx >= 0) {
                              updateLine(idx, 'quantity', (form.items[idx].quantity || 1) + addItemFlyout.quantity);
                            } else {
                              setForm((f) => ({
                                ...f,
                                items: [...f.items, { productId: p._id, productName: p.name, sku: p.sku || '', unitOfMeasurement: p.unitOfMeasurement || 'pc', quantity: addItemFlyout.quantity, unitCost: addItemFlyout.price, discountPercent: 0, discountAmount: 0, quantityOnHandAfter: '', comments: [], ocrDescription: '' }]
                              }));
                            }
                          }
                          setAddItemFlyout(null);
                        }}
                        className="px-3 py-1.5 rounded text-sm bg-indigo-600 text-white"
                      >
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {/* Right: document items table */}
              <div className="flex-1 overflow-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100 border-b">
                      <th className="text-left p-2">ID</th>
                      <th className="text-left p-2">Code</th>
                      <th className="text-left p-2">Name</th>
                      {form.type === 'inventory_count' ? (
                        <>
                          <th className="text-left p-2">Unit</th>
                          <th className="text-right p-2">Expected</th>
                          <th className="text-right p-2">Counted</th>
                          <th className="text-right p-2">Change</th>
                        </>
                      ) : (
                        <>
                          <th className="text-left p-2">Unit</th>
                          <th className="text-right p-2">Quantity</th>
                          <th className="text-right p-2">Price before tax</th>
                          <th className="text-right p-2">Tax</th>
                          <th className="text-right p-2">Price</th>
                          <th className="text-right p-2">Discount</th>
                          <th className="text-right p-2">Total before tax</th>
                          <th className="text-right p-2">Total</th>
                        </>
                      )}
                      <th className="w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((item, idx) => {
                      const product = products.find((p) => p._id === item.productId);
                      if (form.type === 'inventory_count') {
                        const expected = Number(item.quantityOnHandBefore ?? product?.quantityOnHand ?? 0);
                        const counted = Number(item.quantityOnHandAfter ?? expected);
                        const change = counted - expected;
                        return (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-2 text-gray-500">{idx + 1}</td>
                            <td className="p-2">{product?.sku || '—'}</td>
                            <td className="p-2">{item.productName || product?.name || '—'}</td>
                            <td className="p-2">pc</td>
                            <td className="p-2 text-right text-gray-600">{expected}</td>
                            <td className="p-2 text-right">
                              <input type="number" min="0" value={counted} onChange={(e) => updateLine(idx, 'quantityOnHandAfter', e.target.value)} className="w-20 border rounded px-1 py-0.5 text-right text-sm" />
                            </td>
                            <td className="p-2 text-right">{change >= 0 ? `+${change}` : change}</td>
                            <td className="p-2">
                              <button type="button" onClick={() => removeLine(idx)} className="text-red-600 hover:underline text-xs">Delete</button>
                            </td>
                          </tr>
                        );
                      }
                      const price = Number(item.unitCost ?? product?.price ?? 0);
                      const grossTotal = getLineGrossTotal(item);
                      const netTotal = getLineNetTotal(item);
                      return (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-2 text-gray-500">{idx + 1}</td>
                          <td className="p-2">{item.sku || product?.sku || '—'}</td>
                          <td className="p-2">
                            <div>{item.productName || product?.name || '—'}</div>
                            {(item.comments || []).map((comment, commentIndex) => (
                              <div key={`${comment}-${commentIndex}`} className="text-xs text-gray-500 mt-0.5">{comment}</div>
                            ))}
                          </td>
                          <td className="p-2">{item.unitOfMeasurement || product?.unitOfMeasurement || 'pc'}</td>
                          <td className="p-2 text-right">
                            <input type="number" min="0" step="0.0001" value={item.quantity ?? 1} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} className="w-20 border rounded px-1 py-0.5 text-right text-sm" />
                          </td>
                          <td className="p-2 text-right">{formatNumber(price)}</td>
                          <td className="p-2 text-right">0.00</td>
                          <td className="p-2 text-right">
                            <input type="number" min="0" step="0.01" value={item.unitCost ?? ''} onChange={(e) => updateLine(idx, 'unitCost', e.target.value)} className="w-24 border rounded px-1 py-0.5 text-right text-sm" />
                          </td>
                          <td className="p-2 text-right">
                            <div className="flex justify-end gap-1">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={item.discountPercent ?? 0}
                                onChange={(e) => updateLine(idx, 'discountPercent', Math.min(100, Math.max(0, Number(e.target.value) || 0)))}
                                className="w-16 border rounded px-1 py-0.5 text-right text-sm"
                                title="Discount percent"
                              />
                              <input
                                type="number"
                                min="0"
                                max={grossTotal}
                                step="0.01"
                                value={item.discountAmount ?? 0}
                                onChange={(e) => updateLine(idx, 'discountAmount', Math.max(0, Number(e.target.value) || 0))}
                                className="w-20 border rounded px-1 py-0.5 text-right text-sm"
                                title="Discount amount"
                              />
                            </div>
                            <div className="text-[10px] text-gray-500">%, P</div>
                          </td>
                          <td className="p-2 text-right">{formatNumber(netTotal)}</td>
                          <td className="p-2 text-right font-medium">{formatNumber(netTotal)}</td>
                          <td className="p-2">
                            <button type="button" onClick={() => removeLine(idx)} className="text-red-600 hover:underline text-xs">Delete</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {form.items.length === 0 && <p className="p-4 text-gray-500 text-sm">Add items from the product list on the left.</p>}
              </div>
            </div>
          ) : (
            <div className="p-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Payment method</label>
                    <select value={form.paymentMethod || ''} onChange={(e) => setForm((f) => ({ ...f, paymentMethod: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm">
                      <option value="">None</option>
                      <option value="cash">Cash</option>
                      <option value="gcash">GCash</option>
                      <option value="bank">Bank</option>
                      <option value="credit_card">Credit card</option>
                      <option value="charge_invoice">Charge invoice</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Payment status</label>
                    <select value={form.paymentStatus || ''} onChange={(e) => setForm((f) => ({ ...f, paymentStatus: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm">
                      <option value="">None</option>
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Reference</label>
                    <input type="text" value={form.paymentReference || ''} onChange={(e) => setForm((f) => ({ ...f, paymentReference: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Paid amount</label>
                    <input type="number" min="0" step="0.01" value={form.paidAmount || 0} onChange={(e) => setForm((f) => ({ ...f, paidAmount: Number(e.target.value) || 0 }))} className="w-full border rounded px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Change</label>
                    <input type="number" min="0" step="0.01" value={form.changeAmount || 0} onChange={(e) => setForm((f) => ({ ...f, changeAmount: Number(e.target.value) || 0 }))} className="w-full border rounded px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Balance</label>
                    <div className="border rounded px-2 py-1.5 text-sm bg-gray-50">P{formatNumber(Math.max(0, getFormTotal() - Math.min(getFormTotal(), Number(form.paidAmount) || 0)))}</div>
                  </div>
              </div>
            </div>
          )}

          {/* Summary + Internal note */}
          <div className="border-t p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Internal note</label>
              <textarea value={form.reason || ''} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} rows={2} className="w-full border rounded px-2 py-1.5 text-sm" placeholder="Internal note" />
              {form.type === 'proforma' && (
                <label className="mt-2 flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={shouldShowItemPrices(form)}
                    onChange={(e) => setForm((f) => ({
                      ...f,
                      printOptions: {
                        ...(f.printOptions || {}),
                        showItemPrices: e.target.checked
                      }
                    }))}
                    className="mt-1 rounded"
                  />
                  <span>
                    <span className="block font-medium">Show item prices on print/PDF</span>
                    <span className="block text-xs text-gray-500">Turn off to show item names and quantities only, with one final total for the customer.</span>
                  </span>
                </label>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Discount</label>
                <div className="flex">
                  <select
                    value={hasProductDiscounts() ? 'amount' : discountMode}
                    onChange={(e) => {
                      if (hasProductDiscounts()) return;
                      const nextMode = e.target.value;
                      const subtotal = getFormItemsTotal();
                      if (nextMode === 'percent') {
                        const amount = clampDiscountAmount(form.discountAmount);
                        setDiscountPercent(subtotal > 0 ? Number(((amount / subtotal) * 100).toFixed(4)) : 0);
                        setDiscountMode('percent');
                      } else {
                        setForm((f) => ({ ...f, discountAmount: getFormDiscount() }));
                        setDiscountMode('amount');
                      }
                    }}
                    disabled={hasProductDiscounts()}
                    className="border rounded-l px-2 py-1.5 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-500"
                  >
                    <option value="amount">Amount</option>
                    <option value="percent">%</option>
                  </select>
                  <input
                    type="number"
                    min="0"
                    max={discountMode === 'percent' ? 100 : getFormItemsTotal()}
                    step={discountMode === 'percent' ? '0.01' : '0.01'}
                    value={hasProductDiscounts() ? getFormDiscount() : (discountMode === 'percent' ? discountPercent : (form.discountAmount || 0))}
                    onChange={(e) => {
                      if (hasProductDiscounts()) return;
                      const value = Math.max(0, Number(e.target.value) || 0);
                      if (discountMode === 'percent') {
                        setDiscountPercent(Math.min(100, value));
                      } else {
                        setForm((f) => ({ ...f, discountAmount: clampDiscountAmount(value) }));
                      }
                    }}
                    disabled={hasProductDiscounts()}
                    className="w-full border border-l-0 rounded-r px-2 py-1.5 text-sm text-right disabled:bg-gray-100 disabled:text-gray-700"
                  />
                </div>
                {hasProductDiscounts() ? (
                  <div className="mt-1 text-xs text-gray-500 text-right">Product discounts total</div>
                ) : discountMode === 'percent' && (
                  <div className="mt-1 text-xs text-gray-500 text-right">P{formatNumber(getFormDiscount())}</div>
                )}
              </div>
              <div className="flex flex-col justify-end text-sm">
                <span className="text-gray-500">Subtotal</span>
                <strong>{formatNumber(getFormItemsTotal())}</strong>
              </div>
              <div className="flex flex-col justify-end text-sm">
                <span className="text-gray-500">Tax</span>
                <strong>{formatNumber(getFormTax())}</strong>
              </div>
              <div className="flex flex-col justify-end text-sm">
                <span className="text-gray-500">Total</span>
                <strong>{formatNumber(getFormTotal())}</strong>
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-end gap-2 p-4 border-t bg-gray-50">
            {!isLinkedPosDocument && <button type="button" onClick={async () => { if (!window.confirm('Delete this document?')) return; if (editingDocumentId) { try { await deleteDocument(editingDocumentId, authConfig); setActiveTab('list'); setEditingDocumentId(null); await loadDocuments(); } catch (err) { if (err?.response?.status === 401) onUnauthorized?.(err); else setError(err?.response?.data?.error || 'Delete failed.'); } } else { setActiveTab('list'); } }} className="px-4 py-2 rounded text-sm border border-red-200 text-red-700 hover:bg-red-50">
              Delete
            </button>}
            <button type="button" onClick={handleSavePdf} className="px-4 py-2 rounded text-sm border hover:bg-gray-100">Save as PDF</button>
            <button type="button" onClick={openPrintPreview} className="px-4 py-2 rounded text-sm border hover:bg-gray-100">Print preview</button>
            <button type="button" onClick={handlePrint} className="px-4 py-2 rounded text-sm border hover:bg-gray-100">Print</button>
            <button
              type="button"
              onClick={async (e) => {
                e.preventDefault();
                const items = buildDocumentItemsPayload();
                if (items.length === 0) { setError('Add at least one valid item.'); return; }
                setCreating(true); setError('');
                try {
                  const payload = buildDocumentPayload(items);
                  if (editingDocumentId) {
                    const saved = await updateDocument(editingDocumentId, payload, authConfig);
                    setSelectedDoc(saved);
                    await loadDocuments();
                    setActiveTab('list'); setEditingDocumentId(null);
                  } else {
                    const saved = await createDocument(payload, authConfig);
                    setSelectedDoc(saved);
                    await loadDocuments();
                    setActiveTab('list');
                  }
                } catch (err) {
                  if (err?.response?.status === 401) onUnauthorized?.(err);
                  else setError(err?.response?.data?.error || 'Failed to save.');
                } finally { setCreating(false); }
              }}
              disabled={creating}
              className="px-4 py-2 rounded text-sm bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60"
            >
              {creating ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <button type="button" onClick={() => setActiveTab('list')} className="px-3 py-1.5 rounded text-sm border hover:bg-gray-100">
              ← Back to list
            </button>
            <span className="text-gray-500">|</span>
            {DOCUMENT_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.type}
                type="button"
                onClick={() => setForm((f) => ({ ...f, type: opt.type, items: [{ productId: '', quantity: 1, unitCost: '', discountPercent: 0, discountAmount: 0, quantityOnHandAfter: '', ocrDescription: '' }] }))}
                className={`px-3 py-1.5 rounded text-sm ${form.type === opt.type ? 'bg-indigo-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
              >
                New {opt.code ? `${opt.code} - ${opt.label}` : opt.label}
              </button>
            ))}
          </div>
        <form onSubmit={handleCreate} className="border rounded p-4 bg-gray-50 space-y-3">
          <h4 className="font-semibold">{TYPE_LABELS[form.type]}</h4>
          <input type="hidden" value={form.type} readOnly />
          {form.type === 'purchase' && suppliers.length > 0 && (
            <div>
              <label className="text-sm font-medium">Supplier</label>
              <select
                value={form.supplierId}
                onChange={(e) => setForm((f) => ({ ...f, supplierId: e.target.value }))}
                className="border w-full p-2 rounded text-sm mt-1"
              >
                <option value="">None</option>
                {suppliers.map((s) => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
          {form.type === 'loss_damage' && (
            <input
              type="text"
              placeholder="Reason (e.g. damaged)"
              className="border w-full p-2 rounded text-sm"
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            />
          )}
          <input
            type="text"
            placeholder="Reference (optional)"
            className="border w-full p-2 rounded text-sm"
            value={form.reference}
            onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
          />
          {(form.type === 'purchase' || form.type === 'stock_return' || form.type === 'refund') && (
            <div className="mb-2">
              <button
                type="button"
                onClick={() => setShowScanOCR((v) => !v)}
                className="text-indigo-600 text-sm font-medium"
              >
                {showScanOCR ? 'Hide scanner' : '📷 Scan document (OCR)'}
              </button>
              {showScanOCR && (
                <div className="mt-2">
                  <ScanDocumentOCR
                    onUse={handleOcrLines}
                    onClose={() => setShowScanOCR(false)}
                  />
                </div>
              )}
            </div>
          )}
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium">Items</span>
              <button type="button" onClick={addLine} className="text-indigo-600 text-sm">+ Add line</button>
            </div>
            {form.items.map((row, index) => {
              const product = products.find((p) => p._id === row.productId);
              const currentQoh = product?.quantityOnHand ?? '-';
              return (
              <div key={index} className="flex flex-wrap gap-2 items-center mb-2">
                <select
                  value={row.productId}
                  onChange={(e) => updateLine(index, 'productId', e.target.value)}
                  className="border rounded p-1.5 text-sm flex-1 min-w-[140px]"
                  required
                >
                  <option value="">{row.ocrDescription ? `Match: ${row.ocrDescription}` : 'Select product'}</option>
                  {products.map((p) => (
                    <option key={p._id} value={p._id}>{p.name} (QOH: {p.quantityOnHand ?? '-'})</option>
                  ))}
                </select>
                {row.ocrDescription && (
                  <span className="text-xs text-gray-500" title="From scanned document">{row.ocrDescription}</span>
                )}
                {form.type === 'inventory_count' ? (
                  <>
                    <span className="text-sm text-gray-500 w-16">Current: {currentQoh}</span>
                    <input
                      type="number"
                      min="0"
                      placeholder="New count"
                      className="border rounded p-1.5 text-sm w-24"
                      value={row.quantityOnHandAfter}
                      onChange={(e) => updateLine(index, 'quantityOnHandAfter', e.target.value)}
                    />
                  </>
                ) : (
                  <>
                    <input
                      type="number"
                      min="1"
                      placeholder="Qty"
                      className="border rounded p-1.5 text-sm w-20"
                      value={row.quantity}
                      onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                    />
                    {(form.type === 'purchase' || form.type === 'stock_return' || form.type === 'sales' || form.type === 'refund' || form.type === 'proforma') && (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={form.type === 'sales' || form.type === 'proforma' ? 'Price' : 'Unit cost'}
                        className="border rounded p-1.5 text-sm w-24"
                        value={row.unitCost}
                        onChange={(e) => updateLine(index, 'unitCost', e.target.value)}
                      />
                    )}
                  </>
                )}
                <button type="button" onClick={() => removeLine(index)} className="text-red-600 text-sm">Remove</button>
              </div>
            ); })}
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={creating}
              className="bg-indigo-600 text-white px-4 py-2 rounded text-sm disabled:opacity-60"
            >
              {creating ? 'Creating...' : 'Create document'}
            </button>
            <button type="button" onClick={() => setActiveTab('list')} className="border px-4 py-2 rounded text-sm">
              Cancel
            </button>
          </div>
        </form>
        </div>
      )}
    </div>
  );
}
